import { merge } from 'lodash';
import {
  ReactElement,
  createContext,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';
import {
  ApplicationLicense,
  Scratchpad,
  ScratchpadBucket,
  useGetScratchpadBucketQuery,
} from '../../../../api/graphql/generated';
import { useGetUnitOfMeasureSystem } from '../../../../api/hooks/user-preference.hooks';
import { useScratchpadServiceContext } from '../../../../components/machine/scratchpad/scratchpad-service-context-provider';
import { EnumType } from '../../../../util/enum';
import { Flow, SystemUnit } from '../model/flow.types';
import { FlowAnalyticService } from '../service/flow-analytic.service';
import { FlowItemService, FlowService } from '../service/service.types';
import { FlowPermissionsContextProvider } from './flow-permissions.hooks';

export interface SaveFlowOptions {
  saveAsDelta?: boolean;
}

type FlowServiceSaveFlow = (
  flowToSave: Flow,
  options?: SaveFlowOptions
) => Promise<Scratchpad | undefined>;

export interface FlowServiceContextValue<
  FlowConfig = Record<string, any>,
  SectionType extends EnumType = EnumType,
> {
  appSlug: string;
  flowService: FlowService<FlowConfig>;
  flowAnalyticService: FlowAnalyticService;
  flowItemService: FlowItemService<FlowConfig, SectionType>;
  flow: Flow<FlowConfig>;
  scratchpadAppId: string;
  saveFlow: FlowServiceSaveFlow;
  masterApplicationDefaultParameterSet?: ScratchpadBucket;
  flowVersion: number;
  system: SystemUnit;
}

export const FlowServiceContext = createContext<
  FlowServiceContextValue<Record<string, any>, EnumType> | undefined
>(undefined);

export interface FlowServiceContextFactoryOptions {
  license: ApplicationLicense;
  scratchpad: Scratchpad;
}

export interface FlowServiceContextFactoryResult<
  FlowConfig,
  SectionType extends EnumType = EnumType,
> {
  flowService: FlowService<FlowConfig>;
  flowAnalyticService: FlowAnalyticService;
  flowItemService: FlowItemService<FlowConfig, SectionType>;
  appSlug: string;
}

export type FlowServiceContextFactoryCallback<
  FlowConfig,
  SectionType extends EnumType = EnumType,
> = (
  options: FlowServiceContextFactoryOptions
) => FlowServiceContextFactoryResult<FlowConfig, SectionType>;

export interface FlowServiceContextProviderProps<
  FlowConfig,
  SectionType extends EnumType = EnumType,
> {
  contextFactory: FlowServiceContextFactoryCallback<FlowConfig, SectionType>;
  children: (
    props: FlowServiceContextValue<FlowConfig, SectionType>
  ) => ReactElement | ReactElement;
}

function FlowServiceContextProvider<
  FlowConfig,
  SectionType extends EnumType = EnumType,
>(
  props: FlowServiceContextProviderProps<FlowConfig, SectionType>
): ReactElement | null {
  const { children, contextFactory } = props;

  const [value, setValue] = useState<
    FlowServiceContextValue<FlowConfig, SectionType> | undefined
  >(undefined);

  const { scratchpad, save } = useScratchpadServiceContext();
  const flow: Flow<FlowConfig> = useMemo(() => {
    let val;
    if (scratchpad?.appData) {
      val = { ...scratchpad.appData };
      if (val.weldParameterSet) {
        /**
         * TODO - remove when all clients are on shared code https://github.com/Hirebotics/beacon/pull/1388
         * This is to convert old welding flows over to the new key.
         * If the flow was saved by the old client, weldParameterSet will have the correct values.
         * If the flow was saved by the new client, both fields will be the same anyways.
         */
        val.defaultParameterSet = val.weldParameterSet;
      }
      // val.items = val.items ?? [];
      // TODO revert to previous code when all clients have transitioned
      val.items =
        (val as Flow<FlowConfig>).items?.map((i) => {
          return {
            ...i,
            parameters: i.parameters.map((p) => {
              return {
                ...p,
                baseValue: p.baseValue ?? p.value,
              };
            }),
          };
        }) ?? [];
    } else {
      val = { items: [] };
    }

    return val;
  }, [scratchpad]);

  const license = scratchpad.license;

  const { data: scratchpadBucketData } = useGetScratchpadBucketQuery({
    variables: {
      input: {
        id: String(scratchpad.bucket1Id),
      },
    },
    skip: !scratchpad.bucket1Id,
  });

  const system = useGetUnitOfMeasureSystem();

  const masterApplicationDefaultParameterSet =
    scratchpadBucketData?.getScratchpadBucket;

  useEffect(() => {
    if (!license) {
      return;
    }

    const { appSlug, flowService, flowAnalyticService, flowItemService } =
      contextFactory({
        license,
        scratchpad,
      });

    const saveFlow: FlowServiceSaveFlow = async (
      flowToSave,
      options
    ): Promise<Scratchpad | undefined> => {
      flowToSave.config = merge(
        flowService.getDefaultFlowConfig(),
        flowToSave.config
      );

      const newFlow = await flowService.preSave(flowToSave);

      return save(
        {
          ...scratchpad,
          appData: newFlow,
        },
        options
      );
    };

    flow.config = merge(flowService.getDefaultFlowConfig(), flow.config);

    setValue({
      appSlug,
      flowService,
      flowAnalyticService,
      flowItemService,
      flow,
      saveFlow,
      scratchpadAppId: scratchpad.appId,
      masterApplicationDefaultParameterSet,
      flowVersion: scratchpad.version,
      system,
    });
  }, [
    flow,
    license,
    save,
    scratchpad,
    masterApplicationDefaultParameterSet,
    contextFactory,
    system,
  ]);

  if (value) {
    return (
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      <FlowServiceContext.Provider value={value as any}>
        <FlowPermissionsContextProvider>
          {children(value)}
        </FlowPermissionsContextProvider>
      </FlowServiceContext.Provider>
    );
  }

  return null;
}

function useFlowServices<
  FlowConfig = Record<string, any>,
  SectionType extends EnumType = EnumType,
>(): FlowServiceContextValue<FlowConfig, SectionType> {
  return useContext(FlowServiceContext) as any as FlowServiceContextValue<
    FlowConfig,
    SectionType
  >;
}

export { FlowServiceContextProvider, useFlowServices };
